import axios from 'axios'

// Configuración base de Axios
export const api = axios.create({
  baseURL: '/api', // Proxy configurado en Vite
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// H4 (Fase B #5): adjunta el token de sesión guardado a cada petición.
// /auth/login no necesita token (aún no existe), pero enviarlo de más ahí
// no hace daño — el backend simplemente no lo usa en ese endpoint.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Import/export de CSV (services/csvImport.ts, services/csvExport.ts) usan
// fetch() directo, no esta instancia de axios (para no forzar
// Content-Type: application/json sobre un multipart/form-data, o para
// poder leer la respuesta como blob) — así que NO pasan por el
// interceptor de arriba y se quedaban sin el token, dando 401 "Falta el
// token de autenticación" en /api/data/import-csv y /api/export/* desde
// que esas rutas se protegieron (Fase B). Este helper se usa en ambos
// para adjuntarlo a mano.
export function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Interceptor para manejar errores globalmente
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)

    if (error.response?.status === 401) {
      // Token ausente/caducado/inválido: no tiene sentido seguir mostrando
      // la página actual con datos a medio cargar — se limpia la sesión y
      // se manda a /login. Redirect "duro" (no via react-router) a
      // propósito: un interceptor de axios no tiene contexto de router, y
      // esto también limpia cualquier estado de React que dependiera de
      // una sesión que ya no es válida.
      localStorage.removeItem('authToken')
      localStorage.removeItem('authUser')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    } else if (error.response?.status === 404) {
      console.error('Endpoint no encontrado:', error.config?.url)
    } else if (error.response?.status >= 500) {
      console.error('Error del servidor:', error.response?.data)
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout de la petición')
    }

    return Promise.reject(error)
  }
)

// Tipos de respuesta de la API
export interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export interface DashboardOverview {
  summary: {
    totalLeads: number
    totalSpend: number
    totalRevenue: number
    profit: number
  }
  kpis: {
    roi: number
    cpl: number
    rpl: number
    profitMargin: number
    validationRate: number
  }
  channels: {
    google: { spend: number; percentage: number }
    microsoft: { spend: number; percentage: number }
    facebook: { spend: number; percentage: number }
    tiktok: { spend: number; percentage: number }
  }
  // Coste y revenue por nivel (H1 / Fase A #3): "producido" = vía campaña
  // [LB] (permite duplicados); "único" = vía cualquier campaña de cliente
  // directo. Ambos usan el mismo gasto real de plataforma como numerador
  // del CPL, así que "único" es el nivel a usar para decisiones de margen
  // real. Falta un 3er nivel ("leads reportados por la plataforma de
  // medios") que no se puede calcular: ningún CSV importado trae leads o
  // conversiones reportadas por Google/Meta/Microsoft/TikTok, solo gasto
  // — ver docs/roadmap-dashboard-2.0.md, sección 7.
  costRevenueByLevel: {
    producido: { leads: number; revenue: number; cpl: number; rpl: number }
    unico: { leads: number; revenue: number; cpl: number; rpl: number }
  }
  metadata: {
    dataSource: string
    totalRecords: number
    lastUpdated: string
    currency: string
  }
}

export interface Campaign {
  name: string
  totalRecords: number
  totalLeads: number
  totalRevenue: number
  validationRate: number
  cpl: number
  // H2 (Fase A #4): capping manual por campaña (D4). null = sin capping
  // configurado — el frontend lo muestra como campo vacío, no como 0.
  capping: number | null
  status: string
}

export interface SourcePage {
  url: string
  totalRecords: number
  totalLeads: number
  totalRevenue: number
  validationRate: number
  // (No hay campo "conversionRate": ninguna fuente trae datos de clics,
  // solo de leads generados — la columna "Conversion" que mostraba esto
  // como un 0.0% fijo se eliminó de la UI en la revisión; mismo motivo
  // por el que "reparto por clics" está deshabilitado en H5.)
  // Cliente Directo vs LB (Fase A #2): totalProducido = vía campaña [LB]
  // (permite duplicados); totalUnico = vía cualquier campaña de cliente
  // directo, agregado por source_page. No es una tasa de deduplicación
  // 1:1 por campaña (totalUnico puede superar a totalProducido si la
  // página tiene varios clientes sin relación con la campaña LB) — ver
  // comentario en dashboardController.ts y docs/roadmap-dashboard-2.0.md.
  // Calculado sin aplicar los filtros source/type.
  totalProducido: number
  totalUnico: number
}

export interface DailyRevenue {
  date: string
  campaign?: string
  revenue: number
  leads: number
  validationRate: number
}

export interface RevenuePerDayData {
  dailyData: DailyRevenue[]
  totalRevenue: number
  totalLeads: number
  avgValidationRate: number
}

// Interfaces para filtros
export interface DashboardFilters {
  startDate?: string
  endDate?: string
  source?: string
  type?: string
  flow?: string
}

// Funciones de la API
export const getDashboardOverview = async (filters?: DashboardFilters): Promise<ApiResponse<DashboardOverview>> => {
  const params = new URLSearchParams()
  
  if (filters?.startDate) params.append('startDate', filters.startDate)
  if (filters?.endDate) params.append('endDate', filters.endDate)
  if (filters?.source && filters.source !== 'all') params.append('source', filters.source)
  if (filters?.type && filters.type !== 'all') params.append('type', filters.type)
  if (filters?.flow && filters.flow !== 'all') params.append('flow', filters.flow)
  
  const queryString = params.toString()
  const url = `/dashboard/overview${queryString ? `?${queryString}` : ''}`
  
  console.log('🔍 Enviando request al dashboard con filtros:', { filters, url })
  const response = await api.get<ApiResponse<DashboardOverview>>(url)
  console.log('📦 Respuesta del dashboard:', response.data)
  return response.data
}

export const getCampaigns = async (filters?: DashboardFilters): Promise<ApiResponse<{ campaigns: Campaign[]; total: number }>> => {
  const params = new URLSearchParams()
  
  if (filters?.startDate) params.append('startDate', filters.startDate)
  if (filters?.endDate) params.append('endDate', filters.endDate)
  if (filters?.source && filters.source !== 'all') params.append('source', filters.source)
  if (filters?.type && filters.type !== 'all') params.append('type', filters.type)
  if (filters?.flow && filters.flow !== 'all') params.append('flow', filters.flow)
  
  const queryString = params.toString()
  const url = `/dashboard/campaigns${queryString ? `?${queryString}` : ''}`
  
  console.log('🔍 Enviando request a campañas con filtros:', { filters, url })
  const response = await api.get<ApiResponse<{ campaigns: Campaign[]; total: number }>>(url)
  console.log('📦 Respuesta de campañas:', response.data)
  return response.data
}

// H2 (Fase A #4): capping manual por campaña. capping: null borra el
// valor configurado (vuelve a "sin capping").
export const setCampaignCapping = async (
  campaign: string,
  capping: number | null
): Promise<ApiResponse<{ campaign: string; capping: number | null }>> => {
  const response = await api.put<ApiResponse<{ campaign: string; capping: number | null }>>(
    '/dashboard/campaigns/capping',
    { campaign, capping }
  )
  return response.data
}

export const getSourcePages = async (filters?: DashboardFilters): Promise<ApiResponse<{ sourcePages: SourcePage[]; total: number }>> => {
  const params = new URLSearchParams()
  
  if (filters?.startDate) params.append('startDate', filters.startDate)
  if (filters?.endDate) params.append('endDate', filters.endDate)
  if (filters?.source && filters.source !== 'all') params.append('source', filters.source)
  if (filters?.type && filters.type !== 'all') params.append('type', filters.type)
  if (filters?.flow && filters.flow !== 'all') params.append('flow', filters.flow)
  
  const queryString = params.toString()
  const url = `/dashboard/source-pages${queryString ? `?${queryString}` : ''}`
  
  console.log('🔍 Enviando request a source pages con filtros:', { filters, url })
  const response = await api.get<ApiResponse<{ sourcePages: SourcePage[]; total: number }>>(url)
  console.log('📦 Respuesta de source pages:', response.data)
  return response.data
}

export const getRevenuePerDay = async (filters?: DashboardFilters): Promise<ApiResponse<RevenuePerDayData>> => {
  const params = new URLSearchParams()

  if (filters?.startDate) params.append('startDate', filters.startDate)
  if (filters?.endDate) params.append('endDate', filters.endDate)
  if (filters?.type && filters.type !== 'all') params.append('type', filters.type)
  if (filters?.flow && filters.flow !== 'all') params.append('flow', filters.flow)

  const queryString = params.toString()
  const url = `/dashboard/revenue-per-day${queryString ? `?${queryString}` : ''}`

  const response = await api.get<ApiResponse<RevenuePerDayData>>(url)
  return response.data
}

export const getApiInfo = async (): Promise<ApiResponse<any>> => {
  const response = await api.get<ApiResponse<any>>('/info')
  return response.data
}

export const testApiConnection = async (): Promise<ApiResponse<any>> => {
  const response = await api.get<ApiResponse<any>>('/test')
  return response.data
}

export default api
